import React, { useEffect, useRef } from 'react';
import { Popper, Box, Typography, Paper } from '@mui/material';
import { CloseOutlined } from '@mui/icons-material';
import { SessionMessage } from '@demind-inc/core';

import './ChatDialog.scss';
import { ChatMessage, ChatLoadingMessage } from '../ChatMessage';
import { ChatInput } from '../ChatInput';
import { ReactComponent as SparkleIcon } from '../../../assets/scheduler/sparkle.svg';
import { useTranslation } from 'react-i18next';

interface ChatDialogProps {
  visible: boolean;
  messages: SessionMessage[];
  onClose: () => void;
  onSendMessage: (message: string) => Promise<string | void>;
  isReplying?: boolean;
  anchorEl?: HTMLElement | null;
}

const ChatDialog: React.FC<ChatDialogProps> = ({
  visible,
  messages,
  onClose,
  onSendMessage,
  isReplying = false,
  anchorEl,
}) => {
  const { t } = useTranslation();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const defaultAnchorEl = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isReplying]);

  const handleClose = () => {
    onClose();
  };

  // Use provided anchorEl or fall back to a default position
  const currentAnchorEl = anchorEl || defaultAnchorEl.current;

  return (
    <>
      {/* Hidden anchor element for default positioning */}
      <div
        ref={defaultAnchorEl}
        style={{ position: 'absolute', bottom: 0, right: 0, width: 0, height: 0 }}
      />

      <Popper
        open={visible}
        anchorEl={currentAnchorEl}
        placement="top-end"
        className="chat-dialog"
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [-240, 10], // Locate chat dialog next left of the calendar view
            },
          },
          {
            name: 'preventOverflow',
            options: {
              altAxis: true,
              tether: false,
              rootBoundary: 'viewport',
              padding: 16,
            },
          },
          {
            name: 'flip',
            options: {
              fallbackPlacements: ['top-start', 'bottom-end', 'bottom-start'],
            },
          },
        ]}
      >
        <Paper className="chat-dialog__paper" elevation={3}>
          <Box className="chat-dialog__header">
            <div className="chat-dialog__header__left">
              <SparkleIcon />
              <Typography className="chat-dialog__header__title">
                {t('task.ai.assistantTitle')}
              </Typography>
            </div>
            <CloseOutlined onClick={handleClose} className="chat-dialog__header__icon" />
          </Box>
          <Box className="chat-dialog__content">
            <Box className="chat-dialog__messages">
              {messages.map((msg, idx) => {
                // Find the last agent message
                const agentMessages = messages.filter((m) => m.role !== 'user');
                const lastAgentMessage = agentMessages.pop();
                const isLastAgentMessage =
                  lastAgentMessage &&
                  msg.messageId === lastAgentMessage.messageId &&
                  agentMessages.length >= 2;

                return (
                  <ChatMessage
                    key={msg.messageId}
                    message={msg.content}
                    isUser={msg.role === 'user'}
                    timestamp={msg.sentAt}
                    isLastMessage={isLastAgentMessage}
                  />
                );
              })}
              {isReplying && <ChatLoadingMessage />}
              <div ref={messagesEndRef} />
            </Box>
            <ChatInput
              onSendMessage={onSendMessage}
              disabled={isReplying}
              placeholder={t('task.ai.input.adjustRoutineExample')}
            />
          </Box>
        </Paper>
      </Popper>
    </>
  );
};

export default ChatDialog;
